/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * API Types - Common Response/Request Types
 * ============================================================================
 *
 * Standard type definitions for API communication.
 * Aligned with Spring Boot backend response format.
 *
 * ============================================================================
 */

/**
 * Standard API success response wrapper
 */
export interface ApiResponse<T> {
  success: true;
  data: T;
  message?: string;
}

/**
 * Standard API error response from backend (legacy format)
 */
export interface ApiErrorResponseLegacy {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
  timestamp: string;
}

/**
 * New API error response format (ErrorResponse from backend)
 * Used for constraint violations and validation errors
 */
export interface ApiErrorResponse {
  success: false;
  status: number;
  errorCode: string;           // e.g., "DUPLICATE_ENTRY", "VALIDATION_ERROR"
  message: string;
  path?: string;
  fieldErrors?: Record<string, string>;  // e.g., { "email": "Already exists" }
  errors?: string[];           // List of error messages
  traceId?: string;            // For debugging/support
  timestamp: string;
}

/**
 * Transformed API error for frontend use
 */
export interface ApiError {
  code: string;
  message: string;
  status: number;
  fieldErrors?: Record<string, string>;  // Direct field-to-message mapping
  details?: Record<string, string[]>;    // Legacy: field-to-messages array
  traceId?: string;                      // For debugging/support
}

/**
 * Spring Data Page response format (VIA_DTO mode)
 * Uses the cleaner DTO serialization format
 */
export interface PaginatedResponse<T> {
  content: T[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

/**
 * Spring Boot Page response format (flat structure from backend)
 * This is what the backend actually returns
 */
export interface SpringPageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first?: boolean;
  last?: boolean;
  empty?: boolean;
}

/**
 * PagedResponse - Backend flat pagination format
 * Alias for SpringPageResponse for clearer naming in new code
 */
export type PagedResponse<T> = SpringPageResponse<T>;

/**
 * Transform Spring Boot page response to frontend PaginatedResponse format.
 * Handles both flat format (SpringPageResponse) and nested format (VIA_DTO mode with 'page' object).
 */
export function transformPageResponse<T>(springPage: SpringPageResponse<T> | PaginatedResponse<T>): PaginatedResponse<T> {
  const content = springPage.content || [];

  // Check if already in PaginatedResponse format (nested 'page' object)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageObj = (springPage as any).page;
  if (pageObj && typeof pageObj === 'object') {
    return {
      content,
      page: {
        size: pageObj.size ?? content.length,
        number: pageObj.number ?? 0,
        totalElements: pageObj.totalElements ?? content.length,
        totalPages: pageObj.totalPages ?? 1,
      },
    };
  }

  // Flat format (SpringPageResponse)
  const flat = springPage as SpringPageResponse<T>;
  return {
    content,
    page: {
      size: flat.size ?? content.length,
      number: flat.number ?? 0,
      totalElements: flat.totalElements ?? content.length,
      totalPages: flat.totalPages ?? 1,
    },
  };
}

/**
 * Simplified pagination info for components
 */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isFirst: boolean;
  isLast: boolean;
}

/**
 * Common pagination query params
 */
export interface PaginationParams {
  [key: string]: unknown;
  page?: number;
  size?: number;
  sort?: string;
}

/**
 * Common filter params for list endpoints
 */
export interface FilterParams {
  [key: string]: unknown;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Combined pagination and filter params
 */
export type ListParams = PaginationParams & FilterParams;

/**
 * Helper to extract pagination info from Spring Page response (VIA_DTO format)
 */
export function extractPaginationInfo<T>(response: PaginatedResponse<T>): PaginationInfo {
  return {
    page: response.page.number,
    pageSize: response.page.size,
    totalElements: response.page.totalElements,
    totalPages: response.page.totalPages,
    isFirst: response.page.number === 0,
    isLast: response.page.number >= response.page.totalPages - 1,
  };
}

/**
 * Standard mutation response (for create/update/delete)
 */
export interface MutationResponse<T = void> {
  success: boolean;
  data?: T;
  message: string;
}

/**
 * Batch operation response
 */
export interface BatchResponse {
  success: boolean;
  processed: number;
  failed: number;
  errors?: Array<{
    id: string;
    error: string;
  }>;
}
